import {
	type ApiMode,
	CONTRACT_PIN,
	createZeroviaClient,
	type Scope,
	type ZeroviaClient,
} from "@agentry/api-client";
import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from "react";
import { type AppConfig, applyModeOverride, loadConfig } from "./config";

export type ContractState =
	| { status: "idle" }
	| { status: "checking" }
	| { status: "ok"; contractVersion: string; contractHash: string }
	| { status: "error"; message: string; kind: "MISMATCH" | "TRANSPORT" };

type ApiContextValue = {
	client: ZeroviaClient;
	mode: ApiMode;
	config: AppConfig;
	contract: ContractState;
	pin: typeof CONTRACT_PIN;
	setMode(mode: ApiMode): void;
};

const ApiContext = createContext<ApiContextValue | null>(null);

export function ApiProvider({
	children,
	client: injected,
	initialMode,
}: {
	children: ReactNode;
	/** Testで決定的なClientを差し込むための注入点。 */
	client?: ZeroviaClient;
	initialMode?: ApiMode;
}) {
	const baseConfig = useMemo(() => {
		const loaded = loadConfig();
		const withSearch =
			typeof window === "undefined" ? loaded : applyModeOverride(loaded, window.location.search);
		return initialMode ? { ...withSearch, apiMode: initialMode } : withSearch;
	}, [initialMode]);

	const [mode, setMode] = useState<ApiMode>(injected?.mode ?? baseConfig.apiMode);
	const [contract, setContract] = useState<ContractState>({ status: "idle" });

	/** Real modeの設定不備。Mockの成功で覆い隠さず、Errorとして表に出すため保持する。 */
	const [misconfigured, setMisconfigured] = useState<string | null>(null);

	const client = useMemo<ZeroviaClient>(() => {
		if (injected) return injected;
		try {
			const created = createZeroviaClient({
				mode,
				apiUrl: baseConfig.apiUrl,
				authUrl: baseConfig.authUrl,
			});
			setMisconfigured(null);
			return created;
		} catch (error) {
			// 画面を白紙にしないためMockで描画は続けるが、「Real接続できている」とは絶対に表示しない。
			setMisconfigured(error instanceof Error ? error.message : String(error));
			return createZeroviaClient({ mode: "mock" });
		}
	}, [injected, mode, baseConfig.apiUrl, baseConfig.authUrl]);

	useEffect(() => {
		let cancelled = false;
		if (misconfigured !== null) {
			setContract({ status: "error", kind: "TRANSPORT", message: misconfigured });
			return;
		}
		if (client.mode === "mock") {
			setContract({
				status: "ok",
				contractVersion: CONTRACT_PIN.contractVersion,
				contractHash: CONTRACT_PIN.contractHash,
			});
			return;
		}
		setContract({ status: "checking" });
		client
			.verifyContract()
			.then((result) => {
				if (cancelled) return;
				setContract({
					status: "ok",
					contractVersion: result.contractVersion,
					contractHash: result.contractHash,
				});
			})
			.catch((error: unknown) => {
				if (cancelled) return;
				const isMismatch = error instanceof Error && error.name === "ContractMismatchError";
				setContract({
					status: "error",
					kind: isMismatch ? "MISMATCH" : "TRANSPORT",
					message: error instanceof Error ? error.message : String(error),
				});
			});
		return () => {
			cancelled = true;
		};
	}, [client, misconfigured]);

	const value = useMemo<ApiContextValue>(
		() => ({ client, mode: client.mode, config: baseConfig, contract, pin: CONTRACT_PIN, setMode }),
		[client, baseConfig, contract],
	);

	return <ApiContext.Provider value={value}>{children}</ApiContext.Provider>;
}

export function useApi(): ApiContextValue {
	const value = useContext(ApiContext);
	if (!value) throw new Error("useApi must be used inside <ApiProvider>");
	return value;
}

export function useClient(): ZeroviaClient {
	return useApi().client;
}

/** 各操作がZerovia接続かMockのみかを返す。UIバッジで使う。 */
export function useBacking(operation: keyof ZeroviaClient): "ZEROVIA" | "MOCK_ONLY" {
	const { client, mode } = useApi();
	// Mock modeでは全操作がMock。Real modeのときだけ操作ごとの裏付けを見る。
	return mode === "mock" ? "MOCK_ONLY" : client.backingOf(operation);
}

export type { Scope };
